// Seat (assistant) permissions — single source of truth for the frontend.
// Mirror lives at deskte-api/src/utils/seatPermissions.ts. Keep keys in sync.
//
// HIPAA Minimum Necessary: every flag defaults to FALSE. The supervising agent
// must explicitly grant access to each module/action for their seat.

export const SEAT_PERMISSION_GROUPS = [
  {
    id: "clients",
    label: { es: "Clientes y Leads", en: "Clients & Leads" },
    icon: "Users",
    actions: [
      { key: "clients.view",   label: { es: "Ver clientes",          en: "View clients" } },
      { key: "clients.edit",   label: { es: "Crear / editar clientes", en: "Create / edit clients" } },
      { key: "clients.export", label: { es: "Exportar todos los clientes (Excel/CSV, pide PIN)", en: "Export all clients (Excel/CSV, asks for PIN)" } },
      // Lissette 2026-10-16. Ver deskte-api/src/utils/seatPermissions.ts.
      { key: "paymentMethods.view", label: { es: "Ver métodos de pago del cliente (tarjetas y cuentas; el número completo pide PIN)", en: "View client payment methods (cards and accounts; the full number asks for PIN)" } },
      { key: "leads.view",     label: { es: "Ver leads",             en: "View leads" } },
      { key: "leads.edit",     label: { es: "Crear / editar leads",  en: "Create / edit leads" } },
      { key: "notes.edit",     label: { es: "Agregar notas a clientes", en: "Add notes to clients" } },
    ],
  },
  {
    id: "policies",
    label: { es: "Pólizas y Comisiones", en: "Policies & Commissions" },
    icon: "Layers",
    actions: [
      { key: "policies.view",  label: { es: "Ver pólizas",           en: "View policies" } },
      { key: "policies.edit",  label: { es: "Crear / editar pólizas", en: "Create / edit policies" } },
      { key: "commissions.view", label: { es: "Ver comisiones",      en: "View commissions" } },
      { key: "commissions.edit", label: { es: "Subir / editar comisiones", en: "Import / edit commissions" } },
      { key: "renewals.view",  label: { es: "Ver renovaciones",      en: "View renewals" } },
    ],
  },
  {
    // ¿QUÉ RAMOS ve este asiento? Al revés que el resto: si no marcas ninguna,
    // los ve TODOS. Marcar una o varias lo limita a esas (Lissette 2026-08-17:
    // "darle permiso a pólizas pero escoger qué pólizas verá").
    //
    // Se cumple en el SERVIDOR, no escondiendo cosas en pantalla: las pólizas
    // que no le tocan ni siquiera salen de la base de datos, así que tampoco
    // aparecen en la ficha del cliente, ni en comisiones, ni en reportes. Y
    // tampoco puede crear una de un ramo que no ve.
    //
    // Los nombres son los que guarda la base: "Salud" incluye ACA, Part D,
    // Suplementario y Referido; "Vida" incluye Gastos Finales. En la base son
    // el mismo tipo y separarlos exigiría adivinar en las pólizas antiguas.
    id: "policyTypes",
    label: { es: "Ramos que puede ver", en: "Policy types they can see" },
    icon: "Layers",
    note: {
      es: "Si no marcas ninguno, ve TODOS los ramos (como hasta ahora). Marca solo los que quieras limitarle.",
      en: "If you tick none, they see EVERY type (as until now). Tick only the ones you want to limit them to.",
    },
    actions: [
      { key: "policyTypes.HEALTH",              label: { es: "Salud (ACA, Part D, Suplementario)", en: "Health (ACA, Part D, Supplemental)" } },
      { key: "policyTypes.MEDICARE_ADVANTAGE",  label: { es: "Medicare Advantage",  en: "Medicare Advantage" } },
      { key: "policyTypes.MEDICARE_SUPPLEMENT", label: { es: "Medicare Suplemento", en: "Medicare Supplement" } },
      { key: "policyTypes.LIFE",                label: { es: "Vida (y Gastos Finales)", en: "Life (and Final Expense)" } },
      { key: "policyTypes.DENTAL",              label: { es: "Dental",   en: "Dental" } },
      { key: "policyTypes.VISION",              label: { es: "Visión",   en: "Vision" } },
      { key: "policyTypes.DISABILITY",          label: { es: "Incapacidad / Accidente", en: "Disability / Accident" } },
      { key: "policyTypes.LONG_TERM_CARE",      label: { es: "Cuidado a largo plazo", en: "Long-term care" } },
      { key: "policyTypes.ANNUITY",             label: { es: "Anualidad", en: "Annuity" } },
      { key: "policyTypes.AUTO",                label: { es: "Auto",      en: "Auto" } },
      { key: "policyTypes.HOME",                label: { es: "Hogar",     en: "Home" } },
      { key: "policyTypes.RENTERS",             label: { es: "Inquilinos", en: "Renters" } },
      { key: "policyTypes.FLOOD",               label: { es: "Inundación", en: "Flood" } },
      { key: "policyTypes.UMBRELLA",            label: { es: "Sombrilla", en: "Umbrella" } },
      { key: "policyTypes.COMMERCIAL",          label: { es: "Comercial", en: "Commercial" } },
      { key: "policyTypes.WORKERS_COMP",        label: { es: "Compensación laboral", en: "Workers' comp" } },
      { key: "policyTypes.PET",                 label: { es: "Mascotas",  en: "Pet" } },
      { key: "policyTypes.TRAVEL",              label: { es: "Viaje",     en: "Travel" } },
    ],
  },
  {
    id: "documents",
    label: { es: "Documentos y Firmas", en: "Documents & Signatures" },
    icon: "FileText",
    actions: [
      { key: "documents.view", label: { es: "Ver documentos del cliente", en: "View client documents" } },
      { key: "documents.edit", label: { es: "Subir / eliminar documentos", en: "Upload / delete documents" } },
      { key: "requests.view",  label: { es: "Ver solicitudes de docs", en: "View document requests" } },
      { key: "requests.send",  label: { es: "Enviar solicitudes de docs", en: "Send document requests" } },
      { key: "signatures.view", label: { es: "Ver firmas",           en: "View signatures" } },
      { key: "signatures.send", label: { es: "Enviar firmas",        en: "Send signature requests" } },
    ],
  },
  {
    id: "email",
    label: { es: "Correo", en: "Email" },
    icon: "Mail",
    actions: [
      // Outbound (campaigns, signatures, doc requests, quotes) ALWAYS
      // sends from the supervising agent's mailbox automatically — there
      // is no "view the agent's inbox" or "send on behalf" toggle anymore
      // (removed 2026-05-17). The only email gate left: whether this seat
      // is allowed to connect their OWN mailbox.
      { key: "email.connect", label: { es: "Permitir conectar su propio correo", en: "Allow connecting their own email" } },
    ],
  },
  {
    id: "conversations",
    label: { es: "Conversaciones", en: "Conversations" },
    icon: "MessageSquare",
    actions: [
      { key: "conversations.view",   label: { es: "Ver la bandeja de mensajes",  en: "See the message inbox" } },
      { key: "conversations.reply",  label: { es: "Contestar a los clientes",    en: "Reply to clients" } },
      { key: "conversations.assign", label: { es: "Repartir conversaciones",     en: "Assign conversations" } },
    ],
  },
  {
    id: "campaigns",
    label: { es: "Campañas y Secuencias", en: "Campaigns & Sequences" },
    icon: "Megaphone",
    actions: [
      { key: "campaigns.view", label: { es: "Ver campañas",          en: "View campaigns" } },
      { key: "campaigns.edit", label: { es: "Crear campañas",        en: "Create campaigns" } },
      { key: "sequences.view", label: { es: "Ver secuencias",        en: "View sequences" } },
      { key: "sequences.edit", label: { es: "Crear / editar secuencias", en: "Create / edit sequences" } },
    ],
  },
  {
    id: "billing",
    label: { es: "Cotizaciones y Facturas", en: "Quotes & Invoices" },
    icon: "Receipt",
    actions: [
      { key: "quotes.view",    label: { es: "Ver cotizaciones",      en: "View quotes" } },
      { key: "quotes.edit",    label: { es: "Crear cotizaciones",    en: "Create quotes" } },
      { key: "invoices.view",  label: { es: "Ver facturas",          en: "View invoices" } },
      { key: "invoices.edit",  label: { es: "Crear facturas",        en: "Create invoices" } },
      { key: "recurring.view", label: { es: "Ver recurrentes",       en: "View recurring" } },
      { key: "recurring.edit", label: { es: "Gestionar recurrentes", en: "Manage recurring" } },
    ],
  },
  {
    id: "ops",
    label: { es: "Operaciones", en: "Operations" },
    icon: "ListTodo",
    actions: [
      { key: "tasks.view",     label: { es: "Ver tareas",            en: "View tasks" } },
      { key: "tasks.edit",     label: { es: "Crear / editar tareas", en: "Create / edit tasks" } },
      { key: "calendar.view",  label: { es: "Ver calendario",        en: "View calendar" } },
      { key: "calendar.edit",  label: { es: "Editar calendario",     en: "Edit calendar" } },
      { key: "calendar.viewPrivateDetails", label: { es: "Ver detalles de mi calendario personal (no solo \"Ocupado\")", en: "See my personal calendar details (not just \"Busy\")" } },
      { key: "workflows.view", label: { es: "Ver automatizaciones",  en: "View workflows" } },
      { key: "workflows.edit", label: { es: "Gestionar automatizaciones", en: "Manage workflows" } },
      { key: "forms.view",     label: { es: "Ver formularios",       en: "View forms" } },
      { key: "forms.edit",     label: { es: "Gestionar formularios", en: "Manage forms" } },
      { key: "templates.view", label: { es: "Ver plantillas",        en: "View templates" } },
      { key: "templates.edit", label: { es: "Editar plantillas",     en: "Edit templates" } },
      { key: "resources.view", label: { es: "Ver recursos",          en: "View resources" } },
      { key: "resources.edit", label: { es: "Subir / editar recursos", en: "Upload / edit resources" } },
    ],
  },
  {
    id: "reports",
    label: { es: "Análisis y Metas", en: "Analytics & Goals" },
    icon: "BarChart2",
    actions: [
      { key: "reports.view",   label: { es: "Ver reportes y análisis", en: "View reports & analytics" } },
      { key: "goals.view",     label: { es: "Ver metas",             en: "View goals" } },
    ],
  },
  {
    // ¿QUÉ REPORTES ve este asiento? (Lissette 2026-10-11: "crear también
    // permisos para los seats: qué reporte ver y cuáles no"). Desde el
    // 2026-10-30 ve SOLO los que estén marcados (*"sale Sales Report, no está
    // marcado y aún así lo pueden ver"*): antes, ninguno marcado = todos.
    // Hace falta además "Ver reportes y análisis", menos Comisiones por
    // Analista, que solo enseña lo suyo y no usa los reportes del servidor.
    id: "reportsOnly",
    label: { es: "Qué reportes puede ver", en: "Which reports they can see" },
    icon: "BarChart2",
    note: {
      es: "Solo ve los reportes que marques: lo que no esté marcado no le sale. Necesita también «Ver reportes y análisis» (menos Comisiones por Analista, que solo le enseña lo suyo).",
      en: "They only see the reports you tick: anything unticked doesn't show. Also needs “View reports & analytics” (except Commissions by Analyst, which only shows their own).",
    },
    actions: [
      { key: "reportsOnly.analytics",  label: { es: "Análisis",             en: "Analytics" } },
      { key: "reportsOnly.sales",      label: { es: "Reporte de Ventas",    en: "Sales Report" } },
      { key: "reportsOnly.clients",    label: { es: "Reportes de Clientes", en: "Client Reports" } },
      { key: "reportsOnly.team",       label: { es: "Reportes de Equipo",   en: "Team Reports" } },
      { key: "reportsOnly.members",    label: { es: "Miembros Activos",     en: "Active Members" } },
      { key: "reportsOnly.dashboards", label: { es: "Paneles de Reportes",  en: "Report Dashboards" } },
      { key: "reportsOnly.analystCommissions", label: { es: "Comisiones por Analista (solo lo suyo)", en: "Commissions by Analyst (own only)" } },
    ],
  },
  {
    // The supervising agent's own credentials: state insurance licenses
    // (NPN) and carrier/company appointments. Default OFF — a seat only
    // sees/edits them if the agent explicitly grants it.
    id: "credentials",
    label: { es: "Credenciales del agente", en: "Agent Credentials" },
    icon: "Award",
    actions: [
      { key: "licenses.view",     label: { es: "Ver licencias del agente",          en: "View agent licenses" } },
      { key: "licenses.edit",     label: { es: "Editar licencias del agente",        en: "Edit agent licenses" } },
      { key: "appointments.view", label: { es: "Ver appointments con compañías",     en: "View company appointments" } },
      { key: "appointments.edit", label: { es: "Editar appointments con compañías",  en: "Edit company appointments" } },
      { key: "vault.view",        label: { es: "Ver bóveda de contraseñas",          en: "View password vault" } },
      { key: "vault.edit",        label: { es: "Editar bóveda de contraseñas",        en: "Edit password vault" } },
    ],
  },
  {
    // HealthSherpa — un asiento puede PREPARAR la inscripción, pero nunca
    // decide a nombre de quién sale: el servidor usa la llave del AGENTE
    // DUEÑO DEL CLIENTE, porque HealthSherpa acredita la solicitud a la
    // cuenta cuya llave se usó. Un asiento que trabaja los libros de dos
    // agentes acierta solo, sin elegir nada.
    //
    // Esto NO le abre la pantalla de la llave ni la de emparejar: no tiene
    // cuenta propia en HealthSherpa y no tendría nada que pegar ahí.
    id: "healthsherpa",
    label: { es: "HealthSherpa", en: "HealthSherpa" },
    icon: "HeartPulse",
    actions: [
      { key: "healthsherpa.enroll", label: {
        es: "Crear enlaces de inscripción (salen a nombre del agente dueño del cliente)",
        en: "Create enrollment links (they go out under the client's own agent)",
      } },
    ],
  },
  {
    // Deletion — by default a seat CANNOT delete records (tasks, tickets,
    // clients, policies, etc.). The supervising agent must explicitly grant
    // this. Gated in the UI via canDelete() below; views hide their delete
    // affordances (select-all + bulk delete, row delete) when it's off.
    id: "deletion",
    label: { es: "Eliminar registros", en: "Delete records" },
    icon: "Trash2",
    actions: [
      { key: "records.delete", label: { es: "Permitir borrar registros (tareas, tickets, clientes, etc.)", en: "Allow deleting records (tasks, tickets, clients, etc.)" } },
    ],
  },
  {
    // Privacy / visibility — by default a seat doesn't see their supervisor's
    // activity feed. The supervisor (Lissette etc.) can opt in per-seat
    // when they want their assistant to have full transparency.
    id: "visibility",
    label: { es: "Visibilidad", en: "Visibility" },
    icon: "Eye",
    actions: [
      { key: "activity.viewSupervisor", label: { es: "Ver mi actividad en el feed", en: "See my activity in the feed" } },
    ],
  },
];

// Flat list of all valid permission keys (excluding legacy mailbox).
export const SEAT_PERMISSION_KEYS = SEAT_PERMISSION_GROUPS
  .flatMap((g) => g.actions)
  .filter((a) => !a.legacy)
  .map((a) => a.key);

// Default permissions object — every flag OFF (HIPAA Minimum Necessary).
export function defaultSeatPermissions() {
  return SEAT_PERMISSION_KEYS.reduce((acc, key) => { acc[key] = false; return acc; }, {});
}

// Coerce a stored value (possibly null/undefined/partial) into a complete
// permissions object. Unknown keys are dropped, missing keys become false.
export function normalizeSeatPermissions(input) {
  const base = defaultSeatPermissions();
  if (!input || typeof input !== "object") return base;
  for (const key of SEAT_PERMISSION_KEYS) {
    if (key in input) base[key] = input[key] === true;
  }
  return base;
}

// Quick check helper used by views to gate UI elements.
export function hasSeatPermission(seatPermissions, key) {
  if (!seatPermissions) return false;
  return seatPermissions[key] === true;
}

// Qué reporte es cada pantalla (id del menú → casilla de "Qué reportes puede
// ver"). Espejo en deskte-api/src/utils/seatPermissions.ts.
export const REPORTE_DE_LA_VISTA = {
  reports_analytics: "analytics",
  sales_report: "sales",
  client_reports: "clients",
  team_reports: "team",
  active_members: "members",
  report_dashboards: "dashboards",
  analyst_commissions: "analystCommissions",
};

/**
 * ¿Este asiento puede ver ESTE reporte? Tiene que estar marcada su casilla en
 * "Qué reportes puede ver" (Lissette 2026-10-30: lo que no está marcado no se
 * ve; antes, ninguna marcada = todos). Los reportes que leen datos del
 * servidor piden además "reports.view"; Comisiones por Analista no, porque a
 * un asiento solo le enseña sus propias ventas.
 */
export function seatPuedeVerReporte(seatPermissions, cual) {
  if (!seatPermissions || seatPermissions["reportsOnly." + cual] !== true) return false;
  if (cual === "analystCommissions") return true;
  return hasSeatPermission(seatPermissions, "reports.view");
}

// Cross-cutting delete gate. Non-assistants (owner / agent) can always delete;
// a seat can only delete when the supervising agent granted "records.delete".
// Used by Tasks, Tickets and other views to hide bulk/row delete affordances.
export function canDelete(currentUser) {
  if (!currentUser) return false;
  if (currentUser.role !== "assistant") return true;
  return hasSeatPermission(normalizeSeatPermissions(currentUser.seatPermissions), "records.delete");
}
